import { DynamicMechanicalSample } from "@/pages/laboratory/models/DynamycMechanicalSample";
import { dxtToLocalServerTime } from "@dexteel/mesf-core";
import {
	CellClassParams,
	ColDef,
	ColGroupDef,
	ValueFormatterParams,
} from "ag-grid-enterprise";
import { useMemo } from "react";
import { CatalogMechanicalTest } from "../../../models/CatalogMechanicalTest";
import { CatalogResultSource } from "../../../models/CatalogResultSource";

interface UseTableDataProps {
	resultSources: CatalogResultSource[];
	mechanicalTests: CatalogMechanicalTest[];
}

export const useTableData = ({
	resultSources,
	mechanicalTests,
}: UseTableDataProps) => {
	// Utility functions
	const numberRichFormatter = (
		params: ValueFormatterParams,
		decimals: number,
	) => {
		if (params.value === null || isNaN(params.value)) return "";
		return parseFloat(params.value).toFixed(decimals);
	};

	const lookupResultSourceValue = (
		resultSources: CatalogResultSource[],
		resultSourceId: string,
	) => {
		const id = isNaN(parseInt(resultSourceId)) ? 0 : parseInt(resultSourceId);
		const sampleObject = resultSources.find(
			(resultSource) => resultSource.ResultSourceId === id,
		);
		return sampleObject?.ResultSourceName || "";
	};

	const cellNumberClass = (params: CellClassParams) => {
		if (params.value === null || isNaN(params.value)) return "error-limit";
		const sample: DynamicMechanicalSample = params.data;
		if (!sample?.Limits) return "error-limit";

		const limit = sample.Limits.find(
			(limit) =>
				limit.MechanicalTestId === parseInt(params.colDef.field ?? "0"),
		);
		if (!limit) return "error-limit2";

		const { MinValue: minLimit, MaxValue: maxLimit } = limit;
		return (minLimit !== null && parseFloat(params.value) < minLimit) ||
			(maxLimit !== null && parseFloat(params.value) > maxLimit)
			? "error-limit"
			: "error-limit2";
	};

	// Sample information columns
	const sampleColumns: (ColDef | ColGroupDef)[] = useMemo(
		() => [
			{
				headerName: "Sample",
				type: ["centerAligned"],
				children: [
					{
						field: "HeatName",
						headerName: "Heat",
						minWidth: 78,
						flex: 1,
					},
					{
						field: "SampleSequence",
						headerName: "Seq",
						minWidth: 50,
						flex: 1,
					},
					{
						field: "GradeName",
						headerName: "Grade",
						minWidth: 86,
						flex: 1,
					},
					{
						field: "SectionName",
						headerName: "Section",
						minWidth: 96,
						flex: 1,
					},
					{
						field: "Width",
						headerName: "Width",
						minWidth: 85,
						type: ["numericColumn"],
						valueFormatter: (params) => numberRichFormatter(params, 4),
						headerClass: "stripe-header",
						flex: 1,
					},
					{
						field: "Thickness",
						headerName: "Thickness",
						minWidth: 113,
						type: ["numericColumn"],
						valueFormatter: (params) => numberRichFormatter(params, 4),
						headerClass: "stripe-header",
						flex: 1,
					},
					{
						field: "ResultSourceId",
						headerName: "Source",
						minWidth: 93,
						flex: 1,
						valueFormatter: (params) =>
							lookupResultSourceValue(resultSources, params.value),
					},
					{
						field: "Method",
						headerName: "Method",
						minWidth: 97,
						flex: 1,
					},
				],
			},
		],
		[resultSources],
	);

	// Results columns
	const resultColumns: (ColDef | ColGroupDef)[] = useMemo(
		() => [
			{
				headerName: "Results",
				children: [
					{
						field: "DateTimeResults",
						headerName: "Result",
						valueGetter: (params) =>
							dxtToLocalServerTime(
								`${params.data.DateTimeResults}`,
								"MM/dd/yyyy - HH:mm",
							) || "",
						minWidth: 150,
						flex: 1,
					},
					{
						field: "Technician",
						headerName: "Technician",
						minWidth: 117,
						flex: 1,
					},
				],
			},
		],
		[],
	);

	// Mechanical test columns
	const mechanicalTestColumns = useMemo(() => {
		const columns: ColDef[] = mechanicalTests
			.filter(
				(mechanicalTest) =>
					mechanicalTest.IsActive && mechanicalTest.MechanicatTestId !== 4,
			) // FILTER %RA
			.map((mechanicalTest) => ({
				field: mechanicalTest.MechanicatTestId?.toString(),
				headerName: mechanicalTest.MechanicalTestName,
				minWidth: 116,
				flex: 1,
				headerClass: mechanicalTest.UseInCircular ? "stripe-header" : "",
				valueFormatter: (params) =>
					numberRichFormatter(params, mechanicalTest.Decimals),
				cellClass: cellNumberClass,
			}));

		return [
			{
				headerName: "Mechanical Tests",
				children: columns,
			},
		];
	}, [mechanicalTests]);

	// Combine all columns
	const allColumns = useMemo(
		() => [...sampleColumns, ...mechanicalTestColumns, ...resultColumns],
		[sampleColumns, mechanicalTestColumns, resultColumns],
	);

	// Default column definitions
	const defaultColDef = useMemo(
		() => ({
			flex: 1,
			resizable: true,
			sortable: true,
			filter: true,
			editable: false,
			tooltipComponent: "customTooltip",
			tooltipComponentParams: { color: "#ececec" },
		}),
		[],
	);

	return {
		columns: allColumns,
		defaultColDef,
	};
};
